/**
 * Fähigkeits-Automatik — das Kernversprechen an Gelegenheitsspieler.
 *
 * Automatik und Handeingabe erzeugen denselben Absichtstyp; die Simulation
 * weiß nicht, wer ausgelöst hat (Architektur §4.4).
 *
 * Nach dem ersten Spieltest um drei Dinge erweitert (Ueberarbeitung B9/B9a):
 *
 *  1. **Handbetrieb je Fähigkeit** — schwere Schläge feuern nie von selbst.
 *  2. **Regeln kennen die Gegnerqualität** — kein Glutstoß in eine Ratte.
 *  3. **Automatikgrad** — klar benannt: 'aus', 'grundangriff' (Standard),
 *     'freigegeben'. Der Grundangriff ist die einzige Fähigkeit, die dauerhaft
 *     von allein feuert; sie kostet nichts und hält nie an. Alles, was den
 *     Spieler anhält oder versetzt, ist von der Automatik ausgeschlossen
 *     (`automatikfaehig: false`) — sonst nimmt sie ihm die Bewegung weg.
 */

import type { Vek } from '@pg/protocol';
import type { Regel, Regelwerk } from '@pg/content';
import { rangStufe, type Rang } from '@pg/content';
import type { Automatikgrad, GegnerWesen, Karte, Spieler } from './typen.js';
import { abstandQ, laenge } from './vektor.js';
import { begehbar } from './karte.js';

export interface Lagebild {
  /** Lebende Gegner in Sichtweite, in stabiler Reihenfolge. */
  gegner: readonly GegnerWesen[];
  naechster: GegnerWesen | null;
  naechsterAbstand: number;
  anzahlNah: number;
  /** Höchster Rang in Sichtweite — Grundlage der Qualitätsbedingung. */
  hoechsterRang: Rang;
  eliteInSicht: boolean;
}

export function baueLagebild(
  spieler: Spieler,
  gegner: readonly GegnerWesen[],
  r: Regelwerk,
): Lagebild {
  const sichtQ = r.kampf.sichtweite * r.kampf.sichtweite;
  const nahQ = 3.0 * 3.0;

  const inSicht: GegnerWesen[] = [];
  let naechster: GegnerWesen | null = null;
  let naechsterQ = Infinity;
  let anzahlNah = 0;
  let hoechster: Rang = 'gewoehnlich';

  for (const g of gegner) {
    if (g.tot) continue;
    const dq = abstandQ(spieler.pos, g.pos);
    if (dq > sichtQ) continue;
    inSicht.push(g);
    if (dq < naechsterQ) {
      naechsterQ = dq;
      naechster = g;
    }
    if (dq <= nahQ) anzahlNah++;
    if (rangStufe(g.rang) > rangStufe(hoechster)) hoechster = g.rang;
  }

  return {
    gegner: inSicht,
    naechster,
    naechsterAbstand: naechster ? Math.sqrt(naechsterQ) : Infinity,
    anzahlNah,
    hoechsterRang: hoechster,
    eliteInSicht: rangStufe(hoechster) >= rangStufe('vorarbeiter'),
  };
}

/** Würde diese Regel gerade auslösen? */
export function regelGreift(
  regel: Regel,
  spieler: Spieler,
  lage: Lagebild,
  reichweite: number,
  nahkampfgrenze: number,
): boolean {
  switch (regel.ausloeser) {
    case 'immer':
      break;

    case 'gegner-in-reichweite': {
      if (!lage.naechster) return false;
      if (lage.naechsterAbstand > reichweite) return false;
      break;
    }

    case 'gegner-ausser-nahkampf': {
      if (!lage.naechster) return false;
      if (lage.naechsterAbstand <= nahkampfgrenze) return false;
      if (lage.naechsterAbstand > reichweite) return false;
      break;
    }

    case 'leben-faellt': {
      const anteil = spieler.leben / Math.max(1, spieler.lebenMax);
      if (anteil > (regel.wert ?? 0.5)) return false;
      return true;
    }

    case 'elite-in-sicht': {
      if (!lage.eliteInSicht) return false;
      break;
    }

    default:
      return false;
  }

  if (regel.bedingung === 'mindestens-gegner') {
    return lage.anzahlNah >= (regel.wert ?? 1);
  }
  if (regel.bedingung === 'unter-anteil') {
    const anteil = spieler.leben / Math.max(1, spieler.lebenMax);
    return anteil <= (regel.wert ?? 0.5);
  }
  // Qualitätsbedingung: "nur ab Vorarbeiter", "nur bei Gezeichneten".
  // Das ist die Antwort auf "keine starke Attacke auf Trash-Mobs".
  if (regel.bedingung === 'mindestens-rang') {
    const noetig = (regel.rang ?? 'vorarbeiter') as Rang;
    return rangStufe(lage.hoechsterRang) >= rangStufe(noetig);
  }
  return true;
}

/**
 * Darf diese Fähigkeit von selbst feuern?
 *
 * Zwei Bedingungen, die beide erfüllt sein müssen:
 *
 *  1. **Die Fähigkeit muss es dürfen.** `automatikfaehig` ist false für alles,
 *     was den Spieler anhält oder versetzt. Eine automatisch ausgelöste
 *     Wirkanimation, die einen festnagelt, ist auf Dauer lästig — und eine, die
 *     einen verschiebt, nimmt einem die Position weg. Beides ist genau das,
 *     was Automatik nicht tun darf.
 *  2. **Der Grad muss weit genug gehen.**
 *     'aus'          — nichts
 *     'grundangriff' — nur Platz 1 (kostenlos, hält nie an)
 *     'freigegeben'  — zusätzlich, was der Spieler selbst freigegeben hat
 */
export function automatikErlaubt(
  grad: Automatikgrad,
  istGrundangriff: boolean,
  automatikfaehig: boolean,
  handbetrieb: boolean,
): boolean {
  if (grad === 'aus') return false;
  if (!automatikfaehig) return false;
  if (istGrundangriff) return true;
  if (grad === 'grundangriff') return false;
  return !handbetrieb;
}

/**
 * Sicherheitsprüfung für Bewegungsfähigkeiten.
 *
 * Sie feuern nie automatisch (`automatikfaehig: false`). Diese Prüfung deckt
 * den Fall ab, dass eine künftige Fähigkeit beides können soll — die vier
 * Bedingungen machen aus „tückisch" ein Werkzeug:
 *   1. nur wenn der Spieler gerade steuert
 *   2. in SEINE Richtung, nie in die des Automatikziels
 *   3. nur auf begehbaren Boden
 *   4. nie tiefer in eine Gruppe hinein
 */
export function sprungIstSicher(
  spieler: Spieler,
  karte: Karte,
  gegner: readonly GegnerWesen[],
  weite: number,
  r: Regelwerk,
): boolean {
  const s = r.faehigkeiten.bewegungssprungSicherheit;

  if (s.nurWennSpielerSteuert && !spieler.steuertGerade) return false;
  if (laenge(spieler.richtung) < 0.08) return false;

  const ziel: Vek = {
    x: spieler.pos.x + spieler.richtung.x * weite,
    y: spieler.pos.y + spieler.richtung.y * weite,
  };

  if (s.nurAufBegehbarenBoden && !begehbar(karte, ziel.x, ziel.y)) return false;

  if (s.nichtInDichtereGruppe) {
    const rq = s.gruppenradius * s.gruppenradius;
    let jetzt = 0;
    let nachher = 0;
    for (const g of gegner) {
      if (g.tot) continue;
      if (abstandQ(spieler.pos, g.pos) <= rq) jetzt++;
      if (abstandQ(ziel, g.pos) <= rq) nachher++;
    }
    if (nachher > jetzt) return false;
  }

  return true;
}

export interface Wahl {
  slot: number;
  ziel: Vek | null;
  manuell: boolean;
}

/**
 * Wählt die Fähigkeit dieses Takts.
 *
 *   1. Eine manuelle Anforderung gewinnt immer — sofort, ohne Rücksicht auf Priorität.
 *   2. Sonst die bereite, automatikfähige Fähigkeit mit der höchsten Priorität,
 *      deren Regel greift.
 *   3. Bei Gleichstand entscheidet die Slot-Nummer. Deterministisch, nie zufällig.
 */
export function waehleFaehigkeit(
  spieler: Spieler,
  lage: Lagebild,
  karte: Karte,
  r: Regelwerk,
): Wahl | null {
  // 1. Handeingabe — sie ignoriert Handbetrieb und Automatikgrad vollständig.
  //
  // WICHTIG: Eine Anforderung, die JETZT nicht ausgeführt werden kann, verfällt.
  // Sie wird nicht gemerkt und löst nicht später aus. Ein solches „Gedächtnis"
  // fühlt sich fremdgesteuert an — man drückt, nichts passiert, und zwei
  // Sekunden später schlägt die Figur von selbst zu. Wer zu früh drückt,
  // drückt eben noch einmal.
  let ausgewaehlt: Wahl | null = null;
  for (let i = 0; i < spieler.slots.length; i++) {
    const s = spieler.slots[i];
    if (!s || !s.manuellAngefordert) continue;

    const bereit = s.abklingen <= 0 && spieler.vorrat >= s.faehigkeit.vorrat;
    if (bereit && !ausgewaehlt) {
      ausgewaehlt = { slot: i, ziel: s.manuellZiel, manuell: true };
    } else {
      // Verfallen lassen — nicht aufheben.
      s.manuellAngefordert = false;
      s.manuellZiel = null;
    }
  }
  if (ausgewaehlt) return ausgewaehlt;

  // 2. Automatik
  let besterSlot = -1;
  let bestePrioritaet = -Infinity;

  for (let i = 0; i < spieler.slots.length; i++) {
    const s = spieler.slots[i];
    if (!s) continue;
    s.regelAktiv = false;
    if (s.abklingen > 0) continue;
    if (spieler.vorrat < s.faehigkeit.vorrat) continue;

    const istGrundangriff = i === 0 && (s.faehigkeit.grundangriff ?? false);
    if (
      !automatikErlaubt(
        spieler.automatikgrad,
        istGrundangriff,
        s.faehigkeit.automatikfaehig ?? false,
        s.handbetrieb,
      )
    ) {
      continue;
    }

    const regel = s.faehigkeit.standardregel;
    const reichweite = s.faehigkeit.reichweite ?? r.kampf.sichtweite;
    if (!regelGreift(regel, spieler, lage, reichweite, r.kampf.nahkampfgrenze)) continue;

    s.regelAktiv = true;
    if (regel.prioritaet > bestePrioritaet) {
      bestePrioritaet = regel.prioritaet;
      besterSlot = i;
    }
  }

  if (besterSlot < 0) return null;
  return { slot: besterSlot, ziel: null, manuell: false };
}
